const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Local calendar date key (YYYY-MM-DD) for column filters. */
export function sessionLocalDateKey(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  const year = pick("year");
  const month = pick("month");
  const day = pick("day");
  if (!year || !month || !day) return "";
  return `${year}-${month}-${day}`;
}

export function formatSessionDateLabelFromKey(key: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return key;
  const year = match[1];
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  const label = MONTHS[month - 1];
  if (!label) return key;
  return `${day} ${label} ${year}`;
}

export function formatSessionDateOnly(iso: string, timeZone?: string): string {
  const key = sessionLocalDateKey(iso, timeZone);
  return key.length === 0 ? "" : formatSessionDateLabelFromKey(key);
}

export function formatSessionWhen(iso: string, locale?: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  const day = value("day");
  const month = monthLabel(value("month"));
  const year = value("year");
  const hour = value("hour");
  const minute = value("minute");
  const period = value("dayPeriod").toLowerCase();
  if (!day || !month || !year || !hour || !minute || !period) return "";
  return `${day} ${month} ${year}, ${hour}:${minute} ${period}`;
}

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < KB) return `${Math.floor(bytes)} B`;
  if (bytes < MB) return `${(bytes / KB).toFixed(1)} KB`;
  if (bytes < GB) return `${(bytes / MB).toFixed(1)} MB`;
  return `${(bytes / GB).toFixed(1)} GB`;
}

export function formatDuration(startedAt: string, endedAt: string | null | undefined): string {
  if (!endedAt) return "In progress";
  const start = Date.parse(startedAt);
  const end = Date.parse(endedAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return "In progress";
  const total = Math.floor((end - start) / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  return `${seconds}s`;
}

function monthLabel(raw: string): string {
  const hit = MONTHS.find((month) => month.toLowerCase() === raw.toLowerCase().slice(0, 3));
  return hit ?? raw.replace(".", "");
}

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

function compareDayKeys(left: string, right: string): number {
  return left.localeCompare(right);
}

function localDayBoundaryMs(dateKey: string, edge: "start" | "end", timeZone?: string): number {
  if (!DAY_KEY.test(dateKey)) return Number.NaN;
  const match = DAY_KEY.exec(dateKey)!;
  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  let lo = Date.UTC(y, m, d - 2);
  let hi = Date.UTC(y, m, d + 2);
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const key = sessionLocalDateKey(new Date(mid).toISOString(), timeZone);
    if (compareDayKeys(key, dateKey) < 0) lo = mid + 1;
    else hi = mid;
  }
  if (sessionLocalDateKey(new Date(lo).toISOString(), timeZone) !== dateKey) return Number.NaN;
  if (edge === "start") return lo;
  let endLo = lo;
  let endHi = lo + 48 * 3600_000;
  while (endLo < endHi) {
    const mid = Math.ceil((endLo + endHi) / 2);
    const key = sessionLocalDateKey(new Date(mid).toISOString(), timeZone);
    if (compareDayKeys(key, dateKey) > 0) endHi = mid - 1;
    else endLo = mid;
  }
  return endLo;
}

/** UTC instant for 00:00:00.000 on a local calendar day. */
export function localDayStartUtcIso(dateKey: string, timeZone?: string): string {
  const ms = localDayBoundaryMs(dateKey, "start", timeZone);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : "";
}

/** UTC instant for the last millisecond on a local calendar day. */
export function localDayEndUtcIso(dateKey: string, timeZone?: string): string {
  const ms = localDayBoundaryMs(dateKey, "end", timeZone);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : "";
}
